import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useVeri, tarihSaat } from "@/lib/veri";
import { useOturum } from "@/lib/oturum";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";

/** D-074 (U-073): `parca` bağlantının "#" parçası (Documents › Alerts, satırın kartı); öteki öğelerde yok. */
interface Bildirim { id: string; tip: string; ton: "info" | "warn" | "bad" | "ok"; baslik: string; alt: string; ts: string; yol: string; parca?: string }
interface Veri { bildirimler: Bildirim[]; uyariSayisi: number }

const ton: Record<Bildirim["ton"], string> = {
  info: "bg-info-bg text-info",
  warn: "bg-warn-bg text-warn",
  bad: "bg-bad-bg text-bad",
  ok: "bg-surface-2 text-ink-2",
};

/**
 * Bildirim zili: kayıt tutulan bir kuyruk yok, uyarılar mevcut veriden türetilir.
 * "Okundu" bilgisi tarayıcıda (son bakış zamanı) durur; rozet ondan sonra gelenleri sayar.
 */
export function Bildirimler() {
  const { ben } = useOturum();
  const { veri } = useVeri<Veri>(ben ? "/bildirim" : null);
  const [acik, setAcik] = useState(false);
  const anahtar = ben ? `oasis.bildirim.${ben.id}` : null;
  const [sonBakis, setSonBakis] = useState<string>("");

  useEffect(() => {
    if (!anahtar) return;
    try { setSonBakis(localStorage.getItem(anahtar) ?? ""); } catch { setSonBakis(""); }
  }, [anahtar]);

  const yeniler = useMemo(() => (veri?.bildirimler ?? []).filter((b) => b.tip !== cevir("Event") && (!sonBakis || b.ts > sonBakis)), [veri, sonBakis]);

  const ac = () => {
    setAcik((a) => !a);
    if (!acik && anahtar) {
      const simdi = new Date().toISOString();
      try { localStorage.setItem(anahtar, simdi); } catch { /* özel pencerede yazılamayabilir */ }
      setSonBakis(simdi);
    }
  };

  return (
    <div className="relative">
      <button onClick={ac} aria-label={cevir("Notifications")} aria-expanded={acik} className="relative rounded-md p-2 text-ink-2 hover:bg-surface-2">
        <Bell size={18} />
        {yeniler.length > 0 && (
          <span className="absolute right-0.5 top-0.5 min-w-[16px] rounded-full bg-orange px-1 text-2xs font-bold leading-4 text-white tnum">{yeniler.length}</span>
        )}
      </button>
      {acik && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setAcik(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 max-h-[70vh] w-[min(92vw,380px)] overflow-y-auto rounded-md border border-line bg-surface shadow-[var(--shadow-lg)]">
            <div className="flex items-center justify-between border-b border-line px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("Notifications")}</span>
              <span className="text-xs text-ink-3">{veri?.uyariSayisi ?? 0} {cevir("warning")}</span>
            </div>
            <ul className="divide-y divide-line">
              {(veri?.bildirimler ?? []).map((b) => (
                <li key={b.id}>
                  <Link to={b.yol as "/"} hash={b.parca} onClick={() => setAcik(false)} className="block px-3 py-2.5 hover:bg-surface-2">
                    <span className="flex items-center gap-2">
                      {/* D-074 (U-073): sunucu metni İngilizce gelir, gösterimde çevrilir (değer taşıyan cümleler sözlük kalıbıyla; "Document alert" dahil). */}
                      <span className={cn("rounded px-1.5 py-0.5 text-2xs font-semibold", ton[b.ton])}>{cevir(b.tip)}</span>
                      <span className="truncate text-sm font-medium">{cevir(b.baslik)}</span>
                      <span className="ml-auto shrink-0 text-2xs text-ink-3 tnum">{tarihSaat(b.ts)}</span>
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-ink-2">{cevir(b.alt)}</span>
                  </Link>
                </li>
              ))}
              {!veri?.bildirimler.length && <li className="px-3 py-6 text-center text-sm text-ink-3">{cevir("No notifications.")}</li>}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
